import type { ImportReport } from '@booknest/shared';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useImportGoodreads } from '../hooks/useImportGoodreads.js';

export function ImportPage(): JSX.Element {
  const importMutation = useImportGoodreads();
  const [report, setReport] = useState<ImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const handleFile = async (file: File): Promise<void> => {
    setError(null);
    setReport(null);
    setFileName(file.name);
    try {
      const csv = await file.text();
      const result = await importMutation.mutateAsync(csv);
      setReport(result);
    } catch (err) {
      setError(extractError(err));
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="font-serif text-3xl font-semibold text-brown md:text-4xl xl:text-5xl">
          Importer ma bibliothèque
        </h1>
        <p className="mt-1 font-script text-lg text-brown-soft xl:text-xl">
          depuis ton export Goodreads 📥
        </p>
      </header>

      <GoodreadsInstructions />

      <Dropzone
        fileName={fileName}
        isLoading={importMutation.isPending}
        onFile={(f) => void handleFile(f)}
      />

      {error && (
        <div className="rounded-2xl bg-terracotta/10 p-4 text-sm text-terracotta-dark ring-1 ring-terracotta/30">
          {error}
        </div>
      )}

      {report && <ReportView report={report} />}
    </div>
  );
}

function GoodreadsInstructions(): JSX.Element {
  return (
    <section className="rounded-3xl bg-paper p-5 shadow-sm">
      <p className="font-serif text-base font-semibold text-brown">
        Comment récupérer mon fichier ?
      </p>
      <ol className="mt-3 flex flex-col gap-2 text-sm text-brown-soft">
        <li>
          1. Sur Goodreads, ouvre{' '}
          <span className="font-semibold text-brown">My Books</span> →{' '}
          <span className="font-semibold text-brown">Import and export</span>.
        </li>
        <li>
          2. Clique sur{' '}
          <span className="font-semibold text-brown">Export Library</span> puis
          télécharge le fichier <code className="text-brown">.csv</code>.
        </li>
        <li>3. Dépose-le ci-dessous : statuts, notes et dates sont conservés.</li>
      </ol>
    </section>
  );
}

interface DropzoneProps {
  fileName: string | null;
  isLoading: boolean;
  onFile: (file: File) => void;
}

function Dropzone({ fileName, isLoading, onFile }: DropzoneProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const pickFromEvent = (files: FileList | null): void => {
    const file = files?.[0];
    if (file) onFile(file);
  };

  return (
    <section
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        pickFromEvent(e.dataTransfer.files);
      }}
      className={`flex flex-col items-center gap-3 rounded-3xl border-2 border-dashed p-10 text-center transition-colors ${
        dragging
          ? 'border-terracotta bg-terracotta/5'
          : 'border-cream-dark bg-paper'
      }`}
    >
      <span className="text-4xl" aria-hidden>
        {isLoading ? '🌱' : '📚'}
      </span>
      <p className="text-sm text-brown-soft">
        {isLoading
          ? 'On range tes livres sur les étagères…'
          : fileName
            ? `Fichier sélectionné : ${fileName}`
            : 'Glisse ton export Goodreads ici'}
      </p>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(e) => pickFromEvent(e.target.files)}
      />
      <button
        type="button"
        disabled={isLoading}
        onClick={() => inputRef.current?.click()}
        className="rounded-full bg-terracotta px-5 py-2.5 text-sm font-semibold text-cream-light transition-colors hover:bg-terracotta-dark disabled:cursor-not-allowed disabled:opacity-50"
      >
        Choisir un fichier CSV
      </button>
    </section>
  );
}

function ReportView({ report }: { report: ImportReport }): JSX.Element {
  return (
    <section className="flex flex-col gap-4 rounded-3xl bg-paper p-5 shadow-sm">
      <p className="font-serif text-base font-semibold text-brown">
        Import terminé ✨
      </p>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Ajoutés" value={report.created} tone="sage" />
        <Stat label="Déjà présents" value={report.skipped} tone="brown" />
        <Stat label="Non résolus" value={report.unresolved.length} tone="terracotta" />
      </div>

      {report.unresolved.length > 0 && (
        <details className="rounded-2xl bg-cream-dark/40 p-4">
          <summary className="cursor-pointer text-sm font-semibold text-brown">
            Livres non rattachés ({report.unresolved.length})
          </summary>
          <ul className="mt-2 flex flex-col gap-1 text-xs text-brown-soft">
            {report.unresolved.map((item, i) => (
              <li key={`${item.title}-${i}`}>
                <span className="font-semibold text-brown">{item.title}</span>
                {item.author ? ` — ${item.author}` : ''} · {item.reason}
              </li>
            ))}
          </ul>
        </details>
      )}

      <Link
        to="/library"
        className="self-start rounded-full bg-brown px-5 py-2.5 text-sm font-semibold text-cream-light transition-colors hover:bg-brown-soft"
      >
        Voir mes étagères →
      </Link>
    </section>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'sage' | 'brown' | 'terracotta';
}): JSX.Element {
  const toneClass = {
    sage: 'text-sage-dark',
    brown: 'text-brown',
    terracotta: 'text-terracotta-dark',
  }[tone];
  return (
    <div className="rounded-2xl bg-cream-light p-4 text-center">
      <p className={`font-serif text-2xl font-semibold ${toneClass}`}>{value}</p>
      <p className="mt-1 text-xs text-brown-soft">{label}</p>
    </div>
  );
}

function extractError(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const response = (err as { response?: { data?: { message?: string } } }).response;
    if (response?.data?.message) return response.data.message;
  }
  return "Échec de l'import. Vérifie que le fichier est bien un export Goodreads CSV.";
}
